// The limits of the provenance record — true of this deployment regardless of
// what any particular org's data looks like, so they are stated here rather
// than computed per request.
//
// WHY THIS FILE EXISTS AT ALL. `gaps` in the API payload reports holes the
// server can SEE (an actor that is literally "anonymous", a run with no
// snapshot). The entries below are holes the server cannot see, because they
// are properties of how the record is written: a field that was overwritten
// leaves nothing behind to detect. A provenance surface that showed only the
// detectable holes would present a record as complete when it is structurally
// not, which is the one failure this whole screen exists to avoid.
//
// Each row names the file that makes it true, and `limitsPin.test.ts` asserts
// those files still say what is claimed. Do not soften a sentence here to
// make the screen read better — the reader is checking it against the code.
//
// COUNSEL REVIEW PENDING (docs/business/05 §5.2) — the user-facing copy on
// this screen has not been reviewed by counsel. It deliberately contains no
// compliance, certification or regulatory language, asserts no standard, and
// claims no verification. Keep it that way until that review happens. What
// this record evidences is the reader's auditor's decision, not ours.

export type Limit = {
  /** What the record cannot tell you, in the reader's own terms. */
  limit: string;
  /** Why — the mechanism, not an apology. */
  because: string;
  /** The file that makes it true. Pinned by `limitsPin.test.ts`. */
  source: string;
};

export const STRUCTURAL_LIMITS: Limit[] = [
  {
    limit:
      "Each term carries its CURRENT review decision, not a history of them. Republishing a document, or a model filling an empty definition, overwrites the previous decision in place.",
    because:
      "`review_state` on a published term is a single record with one action, one actor and one timestamp — not a list. Nothing appends.",
    source: "backend/parse/types.py — PublishedReviewState",
  },
  {
    limit:
      "The reviewer's name is whatever the caller said it was. It is not derived from the session or the key that made the request.",
    because:
      "`actor` is an ordinary request-body field on publish and on merge accept/reject, defaulting to \"anonymous\". A decision made with a valid credential is not bound to that credential.",
    source: "backend/api/routes/docs.py — publish(actor: str = Body(...))",
  },
  {
    limit:
      "Withdrawing a document erases its record. Nothing marks that it once existed.",
    because:
      "Unpublishing deletes the published glossary outright and writes nothing in its place, so a withdrawn document and one never published look identical here.",
    source: "backend/api/routes/docs.py — unpublish",
  },
  {
    limit:
      "Refused proposals are not kept. What a model suggested and a reviewer turned down is absent from the published record.",
    because:
      "Publishing skips rejected terms rather than recording the refusal. The in-progress review file holds them only until the next review.",
    source: "backend/api/routes/docs.py — publish_doc",
  },
  {
    limit:
      "Corpus-level canonical definitions are written by a model and are live before anyone reviews them.",
    because:
      "Reconciliation persists the canonical entity set as it runs. Accepting the matching proposal afterwards records the decision; it does not gate what readers already see.",
    source: "backend/process/reconcile.py — reconcile_corpus",
  },
  {
    limit:
      "The model behind a definition is recorded only when a model was actually called for it.",
    because:
      "A workspace-wide chunk cache serves repeated content without a call, and the call log is written best-effort so a storage failure drops the line rather than failing the extraction.",
    source: "backend/extract/llm_call.py — call_structured",
  },
  {
    limit:
      "There is no reader in this record, and there cannot be. It attests to how content was produced, never to who read it.",
    because:
      "No reader identifier of any kind is collected — not a hashed one either. That is a product decision, and the data-collection ledger is its evidence.",
    source: "backend/analytics/schema.sql — events",
  },
];

/** Prose for each gap code the API can return. The API sends its own
 *  `detail`, which is authoritative and is what the screen renders; this is
 *  the short label the summary row needs and the API does not carry. */
export const GAP_LABELS: Record<string, string> = {
  actor_unattributed: "Decision with no named reviewer",
  model_unattributed: "Machine-written text with no model recorded",
  prior_text_discarded: "Edit whose previous text was not kept",
  rejections_not_retained: "Proposal refused without a record of the refusal",
  never_published: "Document with no acceptance record",
  document_missing: "Registered document absent from storage",
  never_reconciled: "Corpus with no reconciliation run recorded",
  run_snapshot_missing: "Reconciliation run whose result was not kept",
  canonical_live_before_review: "Machine-written definition live, unreviewed",
};

export function gapLabel(code: string): string {
  return GAP_LABELS[code] ?? code;
}
